import React, { Component } from 'react';
import { MdCircle } from 'react-icons/md';
import { useMapData } from '@app/contexts/mapData.context';

// Helper function to create SVG marker with shape only (no text)
const createMarker = (
  color: string,
  size: number
): string => {
  const diameter = size * 2;
  const radius = size;
  
  return `
    <svg width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${radius}" cy="${radius}" r="${radius - 1}" fill="${color}" stroke="#ffffff" stroke-width="2" fill-opacity="0.8"/>
    </svg>
  `;
};

// Convert SVG to data URL
const svgToDataUrl = (svgString: string): string => {
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

// Create a functional wrapper component that uses the hook
const GoogleMapWithBubblesSitesWrapper: React.FC<{}> = () => {
  const { state, loadGoogleMaps, loadDashboardData } = useMapData();
  
  React.useEffect(() => {
    // Load Google Maps and dashboard data if not already loaded
    const loadData = async () => {
      const promises = [];
      
      // Only load Google Maps if not already loaded or loading
      if (!state.googleMapsLoaded && !state.googleMapsLoading) {
        promises.push(loadGoogleMaps());
      }
      
      // Load dashboard data if needed
      promises.push(loadDashboardData());
      
      if (promises.length > 0) {
        await Promise.all(promises);
      }
    };
    loadData();
  }, [state.googleMapsLoaded, state.googleMapsLoading, loadGoogleMaps, loadDashboardData]);

  // Pass the cached state to the class component
  return (
    <GoogleMapWithBubblesSites 
      googleMapsLoaded={state.googleMapsLoaded}
      googleMapsLoading={state.googleMapsLoading}
      googleMapsError={state.googleMapsError}
      sites={state.sites}
      sitesLoading={state.dashboardLoading}
      sitesError={state.dashboardError}
    />
  );
};

interface Site {
  id: string;
  name?: string;
  city?: string;
  country?: string;
  state?: string;
  province?: string;
  address1?: string;
  address2?: string;
  postal_code?: string;
  status?: number;
  location?: {
    coordinates?: {
      latitude: number;
      longitude: number;
    };
    city?: string;
  };
  [key: string]: any;
}

interface BubbleMapProps {
  googleMapsLoaded?: boolean;
  googleMapsLoading?: boolean;
  googleMapsError?: string | null;
  sites?: Site[];
  sitesLoading?: boolean;
  sitesError?: string | null;
}

interface BubbleMapState {
  mapLoaded: boolean;
  loadError: string | null;
  tooltipContent: any;
  tooltipPosition: { x: number; y: number };
}

// Status color mapping to match site list
const statusColors: Record<number, string> = {
  0: "#000000", // Unknown - Black (Site has never checked in)
  1: "#ff0000", // Down - Red (Site has checked in but not within the last two minutes)
  2: "#FBC02D", // Warning - Yellow (Site has checked in within the last two minutes but one or more links are not connected)
  3: "#45B649", // Good - Green (Site has checked in within the last two minutes and all links are connected)
  4: "#808080"  // Missing - Gray
};

const generateRandomNumber = () => {
  const statusValues = Object.values(statusColors);
  const randomNumber = Math.floor(Math.random() * statusValues.length);
  return statusValues[randomNumber];
};

class GoogleMapWithBubblesSites extends Component<BubbleMapProps, BubbleMapState> {
  private mapRef = React.createRef<HTMLDivElement>();
  private map: any = null;
  private markers: any[] = [];
  private infoWindow: any = null;

  state: BubbleMapState = {
    mapLoaded: false,
    loadError: null,
    tooltipContent: null,
    tooltipPosition: { x: 0, y: 0 },
  };

  async componentDidMount() {
    // Check if Google Maps is already loaded from cache
    if (this.props.googleMapsLoaded) {
      this.initializeMap();
      this.setState({ mapLoaded: true });
    }
  }

  componentDidUpdate(prevProps: BubbleMapProps) {
    // Handle Google Maps loading state changes
    if (!prevProps.googleMapsLoaded && this.props.googleMapsLoaded) {
      this.initializeMap();
      this.setState({ mapLoaded: true });
    }

    // Handle Google Maps loading errors
    if (!prevProps.googleMapsError && this.props.googleMapsError) {
      this.setState({ loadError: this.props.googleMapsError });
    }

    // Handle sites loading errors
    if (!prevProps.sitesError && this.props.sitesError) {
      this.setState({ loadError: this.props.sitesError });
    }

    // Update markers when sites data changes
    if (this.state.mapLoaded && prevProps.sites !== this.props.sites) {
      this.updateMarkers();
    }
  }

  initializeMap = () => {
    if (!this.mapRef.current) {
      setTimeout(() => this.initializeMap(), 100);
      return;
    }

    if (!(window as any).google) return;

    const google = (window as any).google;
    
    // Create map with the same config as dashboard
    this.map = new google.maps.Map(this.mapRef.current, {
      zoom: 10,
      center: { lat: 30, lng: 0 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      scaleControl: false,
      rotateControl: false,
      panControl: false,
      gestureHandling: 'auto',
      disableDefaultUI: true,
      keyboardShortcuts: false,
      clickableIcons: false,
      styles: [
        {
          "elementType": "geometry",
          "stylers": [{ "color": "#1d2c4d" }]
        },
        {
          "elementType": "labels.text.fill",
          "stylers": [{ "color": "#8ec3b9" }]
        },
        {
          "elementType": "labels.text.stroke",
          "stylers": [{ "color": "#1a3646" }]
        },
        {
          "featureType": "administrative.country",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#4b6878" }]
        },
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [{ "color": "#0e1626" }]
        },
        {
          "featureType": "road",
          "elementType": "geometry",
          "stylers": [{ "color": "#38414e" }]
        },
        {
          "featureType": "road",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#212a37" }]
        },
        {
          "featureType": "road",
          "elementType": "labels.text.fill",
          "stylers": [{ "color": "#9ca5b3" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry",
          "stylers": [{ "color": "#746855" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#1f2835" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "labels.text.fill",
          "stylers": [{ "color": "#f3d19c" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "labels",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "road",
          "elementType": "labels.text",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "road",
          "elementType": "labels.icon",
          "stylers": [{ "visibility": "off" }]
        },
        // Hide all POI (Point of Interest) markers
        {
          "featureType": "poi",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.business",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.attraction",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.government",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.medical",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.park",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.place_of_worship",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.school",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.sports_complex",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "transit",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "transit.line",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "transit.station",
          "stylers": [{ "visibility": "off" }]
        }
      ]
    });

    // Create info window for tooltips
    this.infoWindow = new google.maps.InfoWindow();

    // Add click listener to map to close info window when clicking outside
    this.map.addListener('click', () => {
      this.infoWindow.close();
    });

    // Add markers
    this.updateMarkers();
  };

  updateMarkers = () => {
    if (!this.map || !(window as any).google) return;

    const google = (window as any).google;

    // Clear existing markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    // Group sites by lat/long
    const coordinateGroups: { [key: string]: Site[] } = {};
    this.props.sites
      .filter(site => site.location?.coordinates?.latitude && site.location?.coordinates?.longitude) // Use coordinates from API
      .forEach(site => {
        const coordinateKey = `${site.location.coordinates.latitude},${site.location.coordinates.longitude}`;
        
        if (!coordinateGroups[coordinateKey]) {
          coordinateGroups[coordinateKey] = [];
        }
        coordinateGroups[coordinateKey].push(site);
      });

    // Create markers for each coordinate group
    Object.entries(coordinateGroups).forEach(([coordinateKey, sites]) => {
      const firstSite = sites[0];
      
      const getMarkerColor = (sites: Site[]) => {
        const statuses = sites.map(site => site.status || 0);
        
        // Priority order: Unknown (0), Down (1), Warning (2), Missing (4), Good (3)
        if (statuses.includes(0)) return statusColors[0]; // Unknown - Black
        if (statuses.includes(1)) return statusColors[1]; // Down - Red  
        if (statuses.includes(2)) return statusColors[2]; // Warning - Yellow
        if (statuses.includes(4)) return statusColors[4]; // Missing - Gray
        if (statuses.includes(3)) return statusColors[3]; // Good - Green
        
        // Default to Unknown if no valid status found
        return statusColors[0];
      };

      const markerColor = getMarkerColor(sites);

      // Generate tooltip title showing site names instead of location
      const getMarkerTitle = (sites: Site[]) => {
        if (sites.length === 1) {
          // Single site: show just the site name
          return sites[0].name || sites[0].id;
        } else {
          // Multiple sites: show first site name + count
          const firstName = sites[0].name || sites[0].id;
          return `${firstName} (+${sites.length - 1} more)`;
        }
      };

      const iconSize = Math.min(10 + sites.length * 2, 20); // Increased base size
      const svgIcon = createMarker(markerColor, iconSize);

      const marker = new google.maps.Marker({
        position: { 
          lat: firstSite.location.coordinates.latitude, 
          lng: firstSite.location.coordinates.longitude 
        },
        map: this.map,
        title: `Sites: ${getMarkerTitle(sites)}`,
        icon: {
          url: svgToDataUrl(svgIcon),
          scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
          anchor: new google.maps.Point(iconSize, iconSize),
        }
      });

      // Add click listener for info window
      marker.addListener('click', () => {
        const content = this.createInfoWindowContent(firstSite.location.city, sites);
        this.infoWindow.setContent(content);
        this.infoWindow.open(this.map, marker);
      });

      // Add hover effects
      marker.addListener('mouseover', () => {
        const hoverSize = Math.min(12 + sites.length * 2, 24); // Increased hover size
        const hoverIcon = createMarker(markerColor, hoverSize);
        marker.setIcon({
          url: svgToDataUrl(hoverIcon),
          scaledSize: new google.maps.Size(hoverSize * 2, hoverSize * 2),
          anchor: new google.maps.Point(hoverSize, hoverSize),
        });
      });

      marker.addListener('mouseout', () => {
        marker.setIcon({
          url: svgToDataUrl(svgIcon),
          scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
          anchor: new google.maps.Point(iconSize, iconSize),
        });
      });

      this.markers.push(marker);
    });

    // Auto-fit bounds to show all markers
    this.fitBoundsToAllMarkers();
  };

  createInfoWindowContent = (city: string, sites: Site[]) => {
    const sitesHtml = sites.map(site => 
      `<div style="display: flex; align-items: center; margin: 2px 0;">
        <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${statusColors[site.status || 0]}; margin-right: 5px;"></span>
        ${site.name}
      </div>`
    ).join('');

    return `
      <div style="color: #222; font-family: Arial, sans-serif;">
        <strong>${city}</strong>
        <div style="margin-top: 5px;">
          ${sitesHtml}
        </div>
      </div>
    `;
  };

  fitBoundsToAllMarkers = () => {
    if (this.markers.length > 0) {
      const bounds = new (window as any).google.maps.LatLngBounds();
      this.markers.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      this.map.fitBounds(bounds);
      
      // Apply the same zoom limit as in updateMarkers
      (window as any).google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 15) {
          this.map.setZoom(15);
        }
      });
    }
  };

  render() {
    const { mapLoaded, loadError } = this.state;

    if (loadError) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          backgroundColor: '#808080',
          color: '#fff',
          fontSize: '16px'
        }}>
          {loadError}
        </div>
      );
    }

    if (!mapLoaded) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          backgroundColor: '#808080',
          color: '#fff',
          fontSize: '16px'
        }}>
          Loading Google Maps...
        </div>
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        <div
          ref={this.mapRef}
          style={{
            width: '100%',
            height: '551px',
            backgroundColor: '#808080',
          }}
        />
        <style>{`
          /* Hide Terms link - not required by Google ToS */
          .gm-style a[href*="terms"] {
            display: none !important;
          }
          /* Hide Keyboard shortcuts link - not required */
          .gm-style a[href*="keyboard"] {
            display: none !important;
          }
          /* Remove background from attribution text */
          .gm-style .gmnoprint {
            background: transparent !important;
            background-color: transparent !important;
          }
          .gm-style .gm-style-cc {
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Target the specific attribution container */
          .gm-style .gm-style-cc > div {
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Remove any box shadow or borders */
          .gm-style .gm-style-cc * {
            background: transparent !important;
            background-color: transparent !important;
            box-shadow: none !important;
          }
        `}</style>
      </div>
    );
  }
}


export default GoogleMapWithBubblesSitesWrapper; 